// src/store/auth/authThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../config/api";
import { API_ROUTES } from "../../config/apiRoutes";
import { setToken, getToken, clearToken } from "../auth/authHelpers";
import {jwtDecode} from "jwt-decode";

let logoutTimer = null;

/**
 * Auto logout based on JWT expiration
 */
const setAutoLogout = (token, dispatch) => {
  if (!token) return;

  try {
    const decoded = jwtDecode(token);
    const expiresIn = decoded.exp * 1000 - Date.now();

    console.log("Token expires in (ms):", expiresIn);

    if (expiresIn > 0) {
      if (logoutTimer) {
        clearTimeout(logoutTimer);
      }
      logoutTimer = setTimeout(() => dispatch(logoutUser()), expiresIn);
    } else {
      dispatch(logoutUser());
    }
  } catch (err) {
    console.error("Invalid token:", err);
    dispatch(logoutUser());
  }
};

/**
 * Login
 */
export const loginThunk = createAsyncThunk(
  "auth/login",
  async ({ credentials }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(API_ROUTES.LOGIN, credentials);
      const { message, statusCode, data } = response.data;

      if (statusCode === 200) {
        if (data?.token) {
          setToken(data.token);
          setAutoLogout(data.token, dispatch);
        }

        return {
          success: true,
          message,
          user: data?.user || null,
        };
      }

      return rejectWithValue(message || "Login failed");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Login failed"
      );
    }
  }
);

/**
 * Auto Login / Refresh Session
 */
export const autoLoginThunk = createAsyncThunk(
  "auth/autoLogin",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No token found");

      const response = await api.post(API_ROUTES.AUTO_LOGIN, { token });
      const { message, statusCode, data } = response.data;

      if (statusCode === 200) {
        if (data?.token) {
          setToken(data.token);
          setAutoLogout(data.token, dispatch);
        }

        return {
          success: true,
          message,
          user: data?.user || null,
        };
      }

      return rejectWithValue(message || "Session refresh failed");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Session refresh failed"
      );
    }
  }
);

/**
 * Logout and redirect to login
 */

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { dispatch }) => {  // <--- get dispatch from thunkAPI
    clearToken();
    if (logoutTimer) {
      clearTimeout(logoutTimer);
      logoutTimer = null;
    }

    

    // Reset the Redux store
    dispatch({ type: 'RESET_STORE' });

    // Redirect to login page
    window.location.href = "/login";

    return { success: true, message: "Logged out" };
  }
);


/**
 * Optional: API interceptor to auto logout on 401 responses
 */
export const setupApiInterceptor = (store) => {
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        store.dispatch(logoutUser());
      }
      return Promise.reject(error);
    }
  );
};
